import React from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';

type ProfilePhase = 'idle' | 'running' | 'failed';

interface StepFinalProps {
  businessName: string;
  phase: ProfilePhase;
  elapsedMs: number;
  onComplete: () => void;
}

export function StepFinal({ businessName, phase, elapsedMs, onComplete }: StepFinalProps) {
  if (phase === 'running') {
    return (
      <div className="text-center py-12">
        <div className="flex justify-center mb-6">
          {/* Spinner ring in dibbi-oranje — duidelijk in beweging */}
          <div
            className="w-14 h-14 rounded-full border-4 border-gray-100 animate-spin"
            style={{ borderTopColor: '#FF6B35' }}
            aria-hidden
          />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Je profiel wordt opgebouwd
        </h3>
        {/* Roterende statustekst — geeft inzicht in wat er nu gebeurt.
            CSS-only animatie via tailwind arbitrary class is lastig, dus
            we doen het via inline keyframes. Cyclus: 8s, 4 fases. */}
        <div className="relative h-6 overflow-hidden">
          <div className="profile-loader-status">
            <span>Antwoorden inlezen…</span>
            <span>Tone of voice destilleren…</span>
            <span>Doelgroep uitwerken…</span>
            <span>Profiel afronden…</span>
            <span>Antwoorden inlezen…</span>
          </div>
        </div>
        <style>{`
          .profile-loader-status {
            display: flex;
            flex-direction: column;
            align-items: center;
            animation: profileLoaderRotate 8s ease-in-out infinite;
          }
          .profile-loader-status > span {
            display: block;
            height: 24px;
            line-height: 24px;
            font-size: 14px;
            font-weight: 500;
            color: #374151;
          }
          @keyframes profileLoaderRotate {
            0%, 18%   { transform: translateY(0); }
            25%, 43%  { transform: translateY(-24px); }
            50%, 68%  { transform: translateY(-48px); }
            75%, 93%  { transform: translateY(-72px); }
            100%      { transform: translateY(-96px); }
          }
        `}</style>
        <p className="mt-4 text-sm text-gray-500">
          Dit duurt meestal ongeveer een minuut. Je hoeft niets te doen, je kunt dit scherm open laten staan.
        </p>
        {elapsedMs > 60_000 && (
          <p className="mt-1 text-sm font-medium text-gray-700">Bijna klaar, nog heel even…</p>
        )}
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="text-center py-12 space-y-4">
        <h3 className="text-xl font-bold text-gray-900">Het lukte niet om je profiel te maken</h3>
        <p className="text-gray-600">Je antwoorden zijn bewaard. Probeer het opnieuw, het duurt meestal maar even.</p>
        <button
          onClick={onComplete}
          className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold"
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Je bent bijna klaar!</h3>
            <p className="text-gray-700 mb-4">
              Op basis van jouw antwoorden gaan we een professioneel bedrijfsprofiel maken voor <strong>{businessName}</strong>.
            </p>
            <p className="text-gray-700">
              Dit profiel helpt ons om:
            </p>
            <ul className="mt-2 space-y-1 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Relevante blog ideeën te genereren</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Content te schrijven in jouw tone of voice</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Passende afbeeldingen te vinden</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <button
        onClick={onComplete}
        className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold transition-all shadow-lg shadow-blue-600/20 text-lg"
      >
        <Sparkles className="w-5 h-5" />
        Maak mijn profiel aan
      </button>
    </div>
  );
}
